import { lazy, Suspense, useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { WidgetFullModeProps } from '@/entities/widget';
import { SPECIAL_AGENT_IDS, useAgentCategoryChipColor } from '@/entities/agent';
import { type PersonaDto } from '@/lib/api/personas-client';
import { SidePanel, useSidePanelScroll } from '@/shared/ui/SidePanelWrapper';
import type { AudienceFlowWidgetProps } from '../model/audience-flow-widget.types';
import { PersonaList } from './components/PersonaList';
import { personaLabel } from '@/shared/lib/persona-label';
import { useBackTo } from '@/shared/lib/back-to';

const PersonaDetailV2 = lazy(() =>
  import('./components/PersonaDetailV2').then((m) => ({ default: m.PersonaDetailV2 })),
);

// Phase D — `audienceFlowDeps` injected by the config-layer factory at
// `@/lib/config/agents.config`. The factory
// pre-binds `usePersonas` from `@/features/persona-sparks`,
// `useCurrentAgentId` + `sendOrCreateChat` from `@/features/chats`, and the
// pre-resolved `SPECIAL_AGENT_IDS.AUDIENCE_FLOW_PERSONA_PREFIX` constant.
//
// Round 2 fix (CRITICAL #2): `audienceFlowDeps` is REQUIRED
// (no optional `?? noopAudienceFlowDeps` fallback). `usePersonas` is
// invoked as a hook below — toggling its reference between the real
// `usePersonas` (which consumes `useQuery` slots) and the noop (0
// hooks) would violate the Rules of Hooks. Required prop guarantees a
// stable deps bag — production via the agents.config factory, tests
// via an explicit no-op stub.

// Helper to find initial persona from data
const getInitialPersona = (data?: AudienceFlowWidgetProps['data']): PersonaDto | null => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (data?.selectedPersonaId && data?.personas) {
    return data.personas.find((p) => p.id === data.selectedPersonaId) ?? null;
  }
  return null;
};

export const AudienceFlowWidget = (props: WidgetFullModeProps<AudienceFlowWidgetProps>) => {
  const { t } = useTranslation('audiences');

  const { data, audienceFlowDeps, readOnly } = props;

  // Phase D — REQUIRED `audienceFlowDeps` prop guarantees stable hook
  // identity across renders.
  const {
    usePersonas,
    useCurrentAgentId,
    useIsChatBusy,
    sendOrCreateChat,
    audienceFlowPersonaPrefix,
  } = audienceFlowDeps;

  // MFINS-2795: track the last RESOLVED `selectedPersonaId` from props so we
  // can recompute `currentPersona` when the prop changes without a useEffect.
  // See the reconcile block below for why "resolved" (not "seen") is the bar.
  const [currentPersona, setCurrentPersonaState] = useState<PersonaDto | null>(() =>
    getInitialPersona(data),
  );
  const [lastSelectedPersonaId, setLastSelectedPersonaId] = useState<string | undefined>(
    // Seeded from the same `data` snapshot `getInitialPersona` read. If that
    // snapshot could not resolve the persona (id present, list not yet
    // fetched), the seed is `undefined` so the reconcile below still has work
    // to do once the list arrives.
    () => (getInitialPersona(data) ? data?.selectedPersonaId : undefined),
  );
  const currentAgentId = useCurrentAgentId();
  const isChatBusy = useIsChatBusy();
  const navigate = useNavigate();
  const backTo = useBackTo();

  // "Start conversation" is held from the click until the send settles AND the chat is idle
  // again. The click alone is not enough: the store only reports busy once the SDK accepts the
  // message, and a second click in that gap would fire a second handoff on the same turn.
  const [pendingPersonaId, setPendingPersonaId] = useState<string | null>(null);
  const isSwitching = isChatBusy || pendingPersonaId !== null;

  // Fetch personas from API only if not provided via props
  const { data: fetchedPersonas, isLoading: isFetching } = usePersonas(!data?.personas);

  // Use provided personas or fallback to fetched ones
  const personas = data?.personas ?? fetchedPersonas ?? null;
  const isLoading = !data?.personas && isFetching;

  const listScrollPositionRef = useRef<number>(0);
  const { scrollToTop, getScrollPosition, setScrollPosition } = useSidePanelScroll();

  // MFINS-2795 (derived-inline / Rule 1): reconcile `currentPersona` with the
  // incoming `data.selectedPersonaId` during render instead of syncing it with
  // a useEffect.
  //
  // The gate is keyed on the id ALONE, and `lastSelectedPersonaId` only
  // advances once that id has actually been resolved (or is `undefined`):
  //
  //  - Deep link: the widget can be opened with `selectedPersonaId` but no
  //    `data.personas`, so the id is unresolvable until the personas query
  //    lands. Leaving `lastSelectedPersonaId` un-advanced keeps the gate open,
  //    and the block re-enters on the render where the list arrives. Those
  //    in-between renders set no state, so there is no re-render loop.
  //  - In-widget navigation: because the personas list is NOT part of the gate,
  //    a fresh array identity cannot re-enter this block and clobber a persona
  //    the user opened. That matters — `usePersonasPolling` refetches on an
  //    interval while any persona is `creationStatus === 'processing'`, which
  //    would otherwise close the open detail view mid-read.
  const incomingSelectedPersonaId = data?.selectedPersonaId;
  if (incomingSelectedPersonaId !== lastSelectedPersonaId) {
    if (incomingSelectedPersonaId === undefined) {
      // Selection cleared upstream — advance and drop back to the list view.
      setLastSelectedPersonaId(undefined);
      setCurrentPersonaState(null);
    } else {
      const nextPersona = personas?.find((p) => p.id === incomingSelectedPersonaId);
      // Advance only on a successful resolve; an unresolved id retries next render.
      if (nextPersona) {
        setLastSelectedPersonaId(incomingSelectedPersonaId);
        setCurrentPersonaState(nextPersona);
      }
    }
  }

  // MFINS-2795 (event-handler / Rule 3): scroll wiring folded into the
  // handler that changes `currentPersona`, so it runs exactly once per
  // navigation instead of as a post-render effect.
  // The scroll work stays OUTSIDE the state updater: updaters must be pure,
  // and StrictMode double-invokes them — a second pass would re-read the
  // scroll offset after `scrollToTop()` already ran and overwrite the saved
  // list position with 0.
  const setCurrentPersona = useCallback(
    (next: PersonaDto | null) => {
      const prev = currentPersona;
      const wasInListView = prev === null;
      const isEnteringDetailView = next !== null;
      const isGoingBackToList = next === null && prev !== null;

      if (isEnteringDetailView && wasInListView) {
        listScrollPositionRef.current = getScrollPosition();
        scrollToTop();
      } else if (isEnteringDetailView && !wasInListView) {
        scrollToTop();
      } else if (isGoingBackToList) {
        setScrollPosition(listScrollPositionRef.current);
      }

      setCurrentPersonaState(next);
    },
    [currentPersona, getScrollPosition, scrollToTop, setScrollPosition],
  );

  const handleStartConversation = useCallback(
    (persona: PersonaDto) => {
      if (isSwitching) return;

      const personaToTalkTo = personaLabel(persona);

      setPendingPersonaId(persona.id);
      const sent = sendOrCreateChat(navigate, {
        agentId: currentAgentId,
        prompt:
          data?.promptOverride ||
          t('audienceFlowWidget.audienceFlowWidget.iWantToSpeakWith', {
            persona: personaToTalkTo,
          }),
        inputs: {
          personaId: persona.id,
          metadata: data?.metadata,
        },
        backTo,
      });
      void Promise.resolve(sent).finally(() => setPendingPersonaId(null));
    },
    [
      backTo,
      currentAgentId,
      data?.metadata,
      data?.promptOverride,
      isSwitching,
      navigate,
      sendOrCreateChat,
      t,
    ],
  );

  // The widget's own agent, not `currentAgentId`: while a persona is open that is a
  // `audience-flow-persona-*` sub-agent, which carries no category of its own.
  const categoryChipColor = useAgentCategoryChipColor(SPECIAL_AGENT_IDS.AUDIENCE_FLOW);

  const headerConfig = useMemo(() => {
    // Determine if the current persona is selected in the chat store
    const isCurrentPersonaSelected =
      audienceFlowPersonaPrefix + (currentPersona?.id ?? '') === currentAgentId;

    if (currentPersona) {
      return {
        backButton: {
          label: t('audienceFlowWidget.audienceFlowWidget.back'),
          onClick: () => setCurrentPersona(null),
        },
        // Read-only preview omits "Start Conversation" — it navigates away
        // into a chat. The Back button stays (within-widget navigation).
        ...(readOnly
          ? {}
          : {
              actions: [
                {
                  type: 'button' as const,
                  label: t('audienceFlowWidget.audienceFlowWidget.startConversation'),
                  onClick: () => handleStartConversation(currentPersona),
                  variant: 'contained' as const,
                  disabled: isCurrentPersonaSelected || isSwitching,
                },
              ],
            }),
      };
    }
    return {
      chip: {
        label: t('audienceFlowWidget.audienceFlowWidget.audienceOverview'),
        color: categoryChipColor,
      },
    };
  }, [
    categoryChipColor,
    currentPersona,
    handleStartConversation,
    currentAgentId,
    audienceFlowPersonaPrefix,
    isSwitching,
    readOnly,
    setCurrentPersona,
    t,
  ]);

  const personaListView = useMemo(
    () => (
      <div style={{ display: currentPersona ? 'none' : 'block', height: '100%' }}>
        <PersonaList
          personas={personas}
          onPersonaClick={setCurrentPersona}
          onStartConversation={handleStartConversation}
          isLoading={isLoading}
          useCurrentAgentId={useCurrentAgentId}
          audienceFlowPersonaPrefix={audienceFlowPersonaPrefix}
          isStartConversationDisabled={isSwitching}
          readOnly={readOnly}
        />
      </div>
    ),
    [
      personas,
      currentPersona,
      isLoading,
      handleStartConversation,
      useCurrentAgentId,
      audienceFlowPersonaPrefix,
      isSwitching,
      setCurrentPersona,
      readOnly,
    ],
  );

  const personaDetailView = useMemo(
    () => (
      <div style={{ display: currentPersona ? 'block' : 'none' }}>
        {currentPersona && (
          <Suspense fallback={null}>
            <PersonaDetailV2 persona={currentPersona} />
          </Suspense>
        )}
      </div>
    ),
    [currentPersona],
  );

  return (
    <SidePanel headerConfig={headerConfig}>
      {personaListView}
      {personaDetailView}
    </SidePanel>
  );
};
